// GLOBAL SPRITE CACHE FOR ATTACK EFFECT
const attackSpriteCache = new Map<string, HTMLCanvasElement>();

function getSteamSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `steam-${qSize}`;
    let sprite = attackSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const diameter = qSize * 2;
        sprite.width = diameter + 4;
        sprite.height = diameter + 4;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = sprite.width / 2;
            const centerY = sprite.height / 2;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            // Dark red cloudy color
            grad.addColorStop(0, 'rgba(127, 29, 29, 0.8)'); // Center
            grad.addColorStop(1, 'rgba(127, 29, 29, 0)');   // Edge
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        attackSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getRuneGlowSprite(): HTMLCanvasElement {
    const key = 'rune-glow';
    let sprite = attackSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const r = 3;
        const blur = 15;
        const padding = blur + 2;
        sprite.width = (r * 2) + padding * 2;
        sprite.height = (r * 2) + padding * 2;

        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const cx = sprite.width / 2;
            const cy = sprite.height / 2;
            sCtx.shadowColor = '#ef4444';
            sCtx.shadowBlur = blur;
            sCtx.fillStyle = '#ef4444';
            sCtx.beginPath();
            sCtx.arc(cx, cy, r, 0, Math.PI * 2);
            sCtx.fill();
        }
        attackSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type AttackData = {
    sword: {
        state: 'appear' | 'drag' | 'slice' | 'fade'
        progress: number
        startX: number
        startY: number
        currX: number
        currY: number
        trace: { x: number; y: number; age: number; w: number }[]
        sparks: { x: number; y: number; vx: number; vy: number; life: number; color: string }[]
        debris: { x: number; y: number; vx: number; vy: number; life: number; rot: number; vRot: number }[]
        steam: { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number; color: string }[]
    }
}

export type AttackEffect = {
    kind: 'attackEpic'
    t: number
    x: number
    y: number
    data: AttackData
}

export function createAttackEffect(x: number, y: number, w = 0, h = 0): AttackEffect {
    const cx = window.innerWidth / 2
    const cy = window.innerHeight / 2
    // If w/h provided, target the center of that rect
    const tx = w ? x + w / 2 : x
    const ty = h ? y + h / 2 : y

    return {
        kind: 'attackEpic',
        t: 0,
        x: tx,
        y: ty,
        data: {
            sword: {
                state: 'appear',
                progress: 0,
                startX: cx,
                startY: cy,
                currX: cx,
                currY: cy,
                trace: [],
                sparks: [],
                debris: [],
                steam: []
            }
        }
    }
}

export function renderAttackEffect(
    effect: AttackEffect,
    ctx: CanvasRenderingContext2D
): boolean {
    const tt = effect.t / 600
    const df = (effect as any)._df ?? 1
    // 3D SWORD ANIMATION
    // Phases: Appear -> Drag -> Slice -> Fade
    const data = effect.data.sword
    const targetX = effect.x
    const targetY = effect.y

    // State Management
    if (data.state === 'appear') {
        data.progress += 0.05 * df
        if (data.progress >= 1) {
            data.state = 'drag'
            data.progress = 0
        }
    } else if (data.state === 'drag') {
        data.progress += 0.015 * df
        if (data.progress >= 1) {
            data.state = 'slice'
            data.progress = 0
        }
    } else if (data.state === 'slice') {
        data.progress += 0.08 * df
        if (data.progress >= 1) {
            data.state = 'fade'
            data.progress = 0
        }
    } else if (data.state === 'fade') {
        data.progress += 0.02 * df
        if (data.progress >= 1) {
            return false
        }
    }

    // Physics & Updates
    const dx = targetX - data.startX
    const dy = targetY - data.startY
    const distTotal = Math.hypot(dx, dy)

    if (data.state === 'drag') {
        // Move sword
        // Ease in out
        const t = data.progress
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
        data.currX = data.startX + dx * ease
        data.currY = data.startY + dy * ease

        // Add Trace & Steam
        if (Math.random() < 1.0) {
            data.trace.push({ x: data.currX, y: data.currY, age: 0, w: 15 + Math.random() * 10 })

            // Add Dark Red Steam Particles along the trace
            // "same particles technic as for Steal cloud trace but in the sparking way"
            for (let i = 0; i < 2; i++) {
                data.steam.push({
                    x: data.currX + (Math.random() - 0.5) * 20,
                    y: data.currY + (Math.random() - 0.5) * 20,
                    vx: (Math.random() - 0.5) * 0.5,
                    vy: -0.5 - Math.random() * 0.5, // Float up
                    life: 0,
                    maxLife: 60 + Math.random() * 40,
                    size: 10 + Math.random() * 15,
                    color: Math.random() > 0.5 ? '#7f1d1d' : '#991b1b' // Dark Red (Red 900/800)
                })
            }
        }

        // Add Sparks at ground contact
        for (let k = 0; k < 3; k++) {
            const ang = Math.atan2(dy, dx) + Math.PI + (Math.random() - 0.5)
            const spd = 2 + Math.random() * 4
            data.sparks.push({
                x: data.currX, y: data.currY,
                vx: Math.cos(ang) * spd,
                vy: Math.sin(ang) * spd - 3,
                life: 1.0,
                color: Math.random() > 0.5 ? '#fca5a5' : '#f87171'
            })
        }

        // Add Debris (Table chunks)
        if (Math.random() < 0.3) {
            const ang = Math.atan2(dy, dx) + Math.PI + (Math.random() - 0.5) * 2
            const spd = 1 + Math.random() * 3
            data.debris.push({
                x: data.currX, y: data.currY,
                vx: Math.cos(ang) * spd,
                vy: Math.sin(ang) * spd - 4,
                life: 1.0,
                rot: Math.random() * Math.PI,
                vRot: (Math.random() - 0.5) * 0.2
            })
        }
    }

    // Update Particles
    // Trace
    data.trace.forEach(p => p.age += 0.01 * df)
    data.trace = data.trace.filter(p => p.age < 1)

    // Steam
    for (let i = data.steam.length - 1; i >= 0; i--) {
        const p = data.steam[i];
        p.life += df;
        p.x += p.vx * df;
        p.y += p.vy * df;
        p.size += 0.2 * df;
        p.vx *= Math.pow(0.95, df);

        if (p.life >= p.maxLife) {
            data.steam[i] = data.steam[data.steam.length - 1];
            data.steam.pop();
        }
    }

    // Sparks
    for (let i = data.sparks.length - 1; i >= 0; i--) {
        const p = data.sparks[i];
        p.x += p.vx * df;
        p.y += p.vy * df;
        p.vy += 0.2 * df;
        p.life -= 0.03 * df;

        if (p.life <= 0) {
            data.sparks[i] = data.sparks[data.sparks.length - 1];
            data.sparks.pop();
        }
    }

    // Debris
    for (let i = data.debris.length - 1; i >= 0; i--) {
        const p = data.debris[i];
        p.x += p.vx * df;
        p.y += p.vy * df;
        p.vy += 0.4 * df;
        p.rot += p.vRot * df;
        p.life -= 0.02 * df;

        if (p.life <= 0) {
            data.debris[i] = data.debris[data.debris.length - 1];
            data.debris.pop();
        }
    }


    ctx.save()

    // 0. Draw Steam (Behind Trace)
    data.steam.forEach(p => {
        const alpha = Math.min(0.6, (1 - p.life / p.maxLife))
        ctx.globalAlpha = alpha

        const sprite = getSteamSprite(p.size)
        const offset = sprite.width / 2
        ctx.drawImage(sprite, p.x - offset, p.y - offset)
    })
    ctx.globalAlpha = 1 // Reset

    // 1. Draw Waves (Water Wake style) - Behind everything
    if (data.trace.length > 1) {
        ctx.globalCompositeOperation = 'screen'

        const angle = Math.atan2(dy, dx)
        const nx = -Math.sin(angle)
        const ny = Math.cos(angle)

        // Oldest point (tail) for the gradient base
        const tailX = data.trace[0].x
        const tailY = data.trace[0].y

        // 4 nested ripples to simulate pushback multi-wave look
        for (let w = 0; w < 4; w++) {
            const ageOffset = w * 0.10; // Slightly tighter spacing to fit 4 easily

            const passes = [
                { spread: 250, thickness: 15, baseAlpha: 0.3, color: '239, 68, 68' }, // Outer glow blur
                { spread: 250, thickness: 6, baseAlpha: 0.8, color: '239, 68, 68' },  // Main red line
                { spread: 250, thickness: 2, baseAlpha: 1.0, color: '252, 165, 165' } // White-red hot core
            ]

            passes.forEach(pass => {
                const grad = ctx.createLinearGradient(data.currX, data.currY, tailX, tailY)
                grad.addColorStop(0, `rgba(${pass.color}, ${pass.baseAlpha})`)
                grad.addColorStop(0.5, `rgba(${pass.color}, ${pass.baseAlpha * 0.7})`)
                grad.addColorStop(1, `rgba(${pass.color}, 0)`)

                ctx.strokeStyle = grad
                ctx.lineWidth = pass.thickness
                ctx.lineCap = 'round'
                ctx.lineJoin = 'round'

                if (pass.thickness === 15) {
                    ctx.shadowColor = '#dc2626'
                    ctx.shadowBlur = 15
                } else {
                    ctx.shadowBlur = 0
                }

                // Left arm of the wake
                ctx.beginPath()
                let startedLeft = false;
                for (let k = data.trace.length - 1; k >= 0; k--) {
                    const p = data.trace[k]
                    const ageEff = p.age - ageOffset
                    if (ageEff <= 0) continue

                    // Linear expansion for perfectly straight V-shape waves from the tip
                    const expansion = ageEff * (pass.spread * 0.55) // Narrower spread for acute angle
                    const wx = p.x + nx * expansion
                    const wy = p.y + ny * expansion

                    if (!startedLeft) { ctx.moveTo(wx, wy); startedLeft = true }
                    else { ctx.lineTo(wx, wy) }
                }
                if (startedLeft) ctx.stroke()

                // Right arm of the wake
                ctx.beginPath()
                let startedRight = false;
                for (let k = data.trace.length - 1; k >= 0; k--) {
                    const p = data.trace[k]
                    const ageEff = p.age - ageOffset
                    if (ageEff <= 0) continue

                    // Linear expansion for perfectly straight V-shape waves from the tip
                    const expansion = ageEff * (pass.spread * 0.55)
                    const wx = p.x - nx * expansion
                    const wy = p.y - ny * expansion

                    if (!startedRight) { ctx.moveTo(wx, wy); startedRight = true }
                    else { ctx.lineTo(wx, wy) }
                }
                if (startedRight) ctx.stroke()
            })
        }
        ctx.shadowBlur = 0
        ctx.globalCompositeOperation = 'source-over' // Reset for other layers
    }

    // 2. Draw Debris
    data.debris.forEach(p => {
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)
        ctx.fillStyle = '#78350f' // Wood color
        ctx.fillRect(-3, -3, 6, 6)
        ctx.restore()
    })

    // 3. Draw Sword (Katana)
    // Calculate 3D projection
    // Sword is at data.currX, data.currY

    // Visual position
    let sx = data.currX
    let sy = data.currY
    let sz = 0 // Height off ground
    let tilt = 0 // 0 = vertical

    if (data.state === 'appear') {
        // Falling from sky
        sz = (1 - data.progress) * 500
        tilt = 0.2
    } else if (data.state === 'drag') {
        sz = 0
        tilt = -0.1 // Slight backward tilt while dragging
    } else if (data.state === 'slice') {
        // Slice animation
        const sliceProgress = data.progress
        sx = targetX + Math.cos(Math.atan2(dy, dx)) * (sliceProgress * 100)
        sy = targetY + Math.sin(Math.atan2(dy, dx)) * (sliceProgress * 100)
        sz = 20
        tilt = 0.5 // Slash angle
    } else {
        // Fade
        sx = targetX + Math.cos(Math.atan2(dy, dx)) * 100
        sy = targetY + Math.sin(Math.atan2(dy, dx)) * 100
        sz = 20
        tilt = 0.5
    }

    const opacity = data.state === 'fade' ? 1 - data.progress : 1
    ctx.globalAlpha = opacity

    // Draw Shadow (Half transparent)
    ctx.save()
    ctx.translate(sx, sy)
    ctx.scale(1, 0.3) // Flatten for shadow
    ctx.beginPath()
    ctx.arc(0, 0, 15, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(0,0,0,0.4)'
    ctx.fill()
    ctx.restore()

    // Draw Katana Model
    ctx.save()
    ctx.translate(sx, sy - sz)
    // Rotate to face movement direction
    ctx.rotate(Math.atan2(dy, dx))
    // Tilt (rotation around Z essentially, or X if we consider 3D)
    // Here we simulate tilt by rotating context
    ctx.rotate(tilt)

    // Scale up overall size by 1.3x as requested
    ctx.scale(1.3, 1.3)

    // Drawing relative to (0,0) which is the point entering the table
    // Blade extends UP (-Y)

    // Blade lengthens from 120 to 180 (1.5x)
    // Curved shape: convex side facing movement (Right)
    ctx.beginPath()
    ctx.moveTo(0, 0) // Point entering table
    // Back of blade (concave)
    ctx.quadraticCurveTo(-5, -90, -10, -180) // To Tsuba
    // Edge of blade (convex)
    ctx.lineTo(5, -180) // Width at Tsuba
    ctx.quadraticCurveTo(12, -90, 0, 0) // Curve back to point
    ctx.closePath()

    // Gradient for blade
    const grad = ctx.createLinearGradient(0, 0, 0, -180)
    grad.addColorStop(0, '#e2e8f0') // Light at bottom
    grad.addColorStop(0.5, '#f1f5f9')
    grad.addColorStop(1, '#94a3b8') // Darker near guard
    ctx.fillStyle = grad
    ctx.fill()

    // Hamon (Temper line)
    ctx.beginPath()
    ctx.moveTo(2, -10)
    ctx.quadraticCurveTo(10, -90, 4, -175)
    ctx.strokeStyle = 'rgba(255,255,255,0.7)'
    ctx.lineWidth = 2
    ctx.stroke()

    // Tsuba (Guard) - Gold/Black disc
    ctx.fillStyle = '#1c1917' // Dark iron
    ctx.beginPath()
    ctx.ellipse(-2, -180, 14, 4, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = '#b45309' // Gold rim
    ctx.lineWidth = 1
    ctx.stroke()

    // Tsuka (Handle)
    ctx.fillStyle = '#0f172a' // Dark wrap
    ctx.beginPath()
    // Slight curve following blade
    ctx.moveTo(-10, -180)
    ctx.lineTo(5, -180)
    ctx.lineTo(0, -230) // Pommel end
    ctx.lineTo(-12, -225)
    ctx.closePath()
    ctx.fill()

    // Handle diamond pattern (Tsuka-ito)
    ctx.fillStyle = '#b45309' // Gold underlay
    for (let i = 0; i < 4; i++) {
        ctx.beginPath()
        ctx.arc(-4, -190 - (i * 10), 2, 0, Math.PI * 2)
        ctx.fill()
    }

    // Glowing Rune on blade (near guard)
    const runeSprite = getRuneGlowSprite();
    const runeOffset = runeSprite.width / 2;
    // Moved up proportionally from -100 to -150
    ctx.drawImage(runeSprite, 0 - runeOffset, -150 - runeOffset);

    ctx.restore()

    // 4. Draw Sparks
    data.sparks.forEach(p => {
        ctx.globalAlpha = p.life * opacity
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2)
        ctx.fill()
    })

    // 5. Slice Effect (on Target)
    if (data.state === 'slice') {
        const sliceP = data.progress
        ctx.globalAlpha = 1 - sliceP
        ctx.save()
        ctx.translate(targetX, targetY)
        ctx.rotate(Math.PI / 6) // 30 degrees

        // Slash line
        ctx.beginPath()
        ctx.moveTo(-50 + sliceP * 100, 0)
        ctx.lineTo(50 + sliceP * 100, 0)
        ctx.strokeStyle = '#ef4444'
        ctx.lineWidth = 4
        ctx.stroke()

        // Slash glow
        ctx.shadowColor = '#ef4444'
        ctx.shadowBlur = 20
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 2
        ctx.stroke()

        ctx.restore()
    }

    ctx.restore()
    return true
}
